import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaMagic, FaCheck, FaTrash, FaUpload, FaUsers } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";
import { MathText, MathJaxContext, mathJaxConfig } from "../../components/MathText";
import { formatAiText } from "../../utils/formatAiText";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
const authHeadersJson = () => ({ "Content-Type": "application/json", ...authHeaders() });

const LessonBuilder = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [lesson, setLesson] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [flashcards, setFlashcards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [students, setStudents] = useState(null);
    const [genOptions, setGenOptions] = useState({ mcqCount: 8, theoryCount: 3, flashcardCount: 8 });

    const fetchLesson = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/lessons/mine/${id}`, { headers: authHeaders() });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to load lesson");
            setLesson(data.data.lesson);
            setQuestions(data.data.questions || []);
            setFlashcards(data.data.flashcards || []);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLesson(); }, [id]);

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/lessons/${id}/generate`, {
                method: "POST",
                headers: authHeadersJson(),
                body: JSON.stringify(genOptions),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Generation failed");
            toast.success("Draft content generated — review below");
            fetchLesson();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setGenerating(false);
        }
    };

    const approveQuestion = async (qid) => {
        await fetch(`${API_BASE_URL}/api/teacher-questions/${qid}/approve`, { method: "PATCH", headers: authHeaders() });
        fetchLesson();
    };
    const discardQuestion = async (qid) => {
        await fetch(`${API_BASE_URL}/api/teacher-questions/${qid}`, { method: "DELETE", headers: authHeaders() });
        fetchLesson();
    };
    const approveFlashcard = async (fid) => {
        await fetch(`${API_BASE_URL}/api/lessons/flashcards/${fid}/approve`, { method: "PATCH", headers: authHeaders() });
        fetchLesson();
    };
    const discardFlashcard = async (fid) => {
        await fetch(`${API_BASE_URL}/api/lessons/flashcards/${fid}/discard`, { method: "PATCH", headers: authHeaders() });
        fetchLesson();
    };

    const handlePublish = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/lessons/${id}/publish`, { method: "POST", headers: authHeaders() });
            if (!res.ok) throw new Error("Failed to publish");
            toast.success("Lesson published — subscribed students can now see it");
            fetchLesson();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const loadStudents = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/lessons/${id}/students`, { headers: authHeaders() });
            const data = await res.json();
            setStudents(data.data || []);
        } catch (err) {
            toast.error("Could not load student progress");
        }
    };

    if (loading) return <div className="p-10 text-gray-500">Loading...</div>;
    if (!lesson) return <div className="p-10 text-gray-500">Lesson not found.</div>;

    const draftQuestions = questions.filter((q) => q.review_status === "draft");
    const approvedQuestions = questions.filter((q) => q.review_status === "approved");
    const draftFlashcards = flashcards.filter((f) => f.status === "draft");

    return (
        <MathJaxContext config={mathJaxConfig}>
            <div className="min-h-screen bg-gray-50">
                <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate("/teacher/lessons")} className="text-white"><FaArrowLeft size={18} /></button>
                        <h1 className="text-xl font-bold text-white">{lesson.title}</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={loadStudents} className="text-white flex items-center gap-2 text-sm"><FaUsers /> Progress</button>
                        {lesson.status !== "published" && (
                            <button onClick={handlePublish} className="bg-white text-blue600 px-4 py-2 rounded-lg font-semibold flex items-center gap-2">
                                <FaUpload /> Publish
                            </button>
                        )}
                    </div>
                </nav>

                <div className="max-w-4xl mx-auto p-6 space-y-6">
                    {students && (
                        <div className="bg-white rounded-xl shadow-sm p-4">
                            <h3 className="font-semibold mb-2">Student Progress</h3>
                            {students.length === 0 && <p className="text-sm text-gray-500">No student has opened this lesson yet.</p>}
                            {students.map((s) => (
                                <div key={s.student_id} className="flex justify-between text-sm py-1 border-t first:border-t-0">
                                    <span>{s.name}</span>
                                    <span className="text-gray-500">{s.completed_at ? `Completed · ${s.practice_score ?? "-"}%` : "In progress"}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h3 className="font-semibold text-gray-800 mb-2">Lesson Note</h3>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{lesson.body}</p>
                    </div>

                    {lesson.ai_summary && (
                        <div className="bg-blue-50 rounded-xl p-6">
                            <h3 className="font-semibold text-blue-800 mb-2">AI Summary</h3>
                            <p className="text-sm text-blue-900 whitespace-pre-wrap">{formatAiText(lesson.ai_summary)}</p>
                        </div>
                    )}

                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><FaMagic /> Generate with AI</h3>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                            <label className="text-sm text-gray-600">MCQs
                                <input type="number" min={0} max={20} className="border rounded-lg p-2 w-full mt-1" value={genOptions.mcqCount}
                                    onChange={(e) => setGenOptions({ ...genOptions, mcqCount: e.target.value })} />
                            </label>
                            <label className="text-sm text-gray-600">Theory
                                <input type="number" min={0} max={10} className="border rounded-lg p-2 w-full mt-1" value={genOptions.theoryCount}
                                    onChange={(e) => setGenOptions({ ...genOptions, theoryCount: e.target.value })} />
                            </label>
                            <label className="text-sm text-gray-600">Flashcards
                                <input type="number" min={0} max={20} className="border rounded-lg p-2 w-full mt-1" value={genOptions.flashcardCount}
                                    onChange={(e) => setGenOptions({ ...genOptions, flashcardCount: e.target.value })} />
                            </label>
                        </div>
                        <button disabled={generating} onClick={handleGenerate} className="bg-blue600 text-white px-5 py-2 rounded-lg font-semibold disabled:opacity-50">
                            {generating ? "Generating..." : "Generate practice, summary & flashcards"}
                        </button>
                    </div>

                    {draftQuestions.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-gray-800 mb-3">Pending Questions ({draftQuestions.length})</h3>
                            <div className="space-y-3">
                                {draftQuestions.map((q) => (
                                    <div key={q.id} className="bg-white rounded-xl shadow-sm p-4 flex justify-between items-start gap-4">
                                        <div className="flex-1">
                                            <span className="text-xs text-gray-400">{q.question_type}</span>
                                            <MathText text={q.question} className="block font-medium text-gray-800" />
                                            {q.question_type === "mcq" && (
                                                <ul className="mt-1 text-sm text-gray-600">
                                                    {["A", "B", "C", "D"].map((l) => (
                                                        <li key={l} className={q.correctAnswer === l ? "text-green-600 font-semibold" : ""}>
                                                            {l}. <MathText text={q[`option_${l.toLowerCase()}`]} />
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                            {q.question_type === "theory" && q.explanation && (
                                                <p className="text-sm text-gray-500 mt-1 whitespace-pre-wrap">Model answer: {formatAiText(q.explanation)}</p>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <button onClick={() => approveQuestion(q.id)} className="text-green-600 hover:bg-green-50 p-2 rounded-lg"><FaCheck /></button>
                                            <button onClick={() => discardQuestion(q.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg"><FaTrash /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {draftFlashcards.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-gray-800 mb-3">Pending Flashcards ({draftFlashcards.length})</h3>
                            <div className="grid grid-cols-2 gap-3">
                                {draftFlashcards.map((f) => (
                                    <div key={f.id} className="bg-white rounded-xl shadow-sm p-4">
                                        <p className="font-medium text-gray-800 text-sm">{f.front}</p>
                                        <p className="text-sm text-gray-500 mt-1">{f.back}</p>
                                        <div className="flex gap-2 mt-2">
                                            <button onClick={() => approveFlashcard(f.id)} className="text-green-600 hover:bg-green-50 p-2 rounded-lg"><FaCheck /></button>
                                            <button onClick={() => discardFlashcard(f.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg"><FaTrash /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <h3 className="font-semibold text-gray-800 mb-3">Approved Practice ({approvedQuestions.length})</h3>
                        {approvedQuestions.length === 0 && <p className="text-sm text-gray-500">Nothing approved yet.</p>}
                        <div className="space-y-2">
                            {approvedQuestions.map((q) => (
                                <div key={q.id} className="bg-white rounded-xl shadow-sm p-3">
                                    <MathText text={q.question} className="text-sm text-gray-700" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </MathJaxContext>
    );
};

export default LessonBuilder;
